import React, { useEffect, useMemo, useState } from 'react';
import PriceChart from './PriceChart';
import AddAssetDialog from './AddAssetDialog';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface Asset {
  id: number;
  symbol: string;
  name: string;
  type: string;
  currency: string;
  region: string | null;
  exchange: string | null;
  sector: string | null;
  isin: string | null;
  wkn: string | null;
  current_price: number | null;
  change_pct_24h: number | null;
  eod_price: number | null;
  eod_date: string | null;
  candle_count: number;
}

interface AssetTableProps {
  type: string;
  title: string;
  icon: string;
  chartType: 'stock' | 'index' | 'bond';
}

type SortField = 'symbol' | 'name' | 'region' | 'wkn' | 'isin';
type SortDir = 'asc' | 'desc';

const SORTABLE_BY_TYPE: Record<string, SortField[]> = {
  stock: ['symbol', 'name', 'region', 'wkn', 'isin'],
  bond: ['symbol', 'name', 'wkn', 'isin'],
  commodity: ['symbol', 'name'],
  index: ['symbol', 'name'],
  crypto: ['symbol', 'name'],
  forex: ['symbol', 'name'],
};

const COL_STYLE: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
};

const HEADER_STYLE: React.CSSProperties = {
  ...COL_STYLE,
  color: '#4A5068',
  fontWeight: 700,
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #1E2330',
};

const FILTER_SELECT_STYLE: React.CSSProperties = {
  padding: '9px 12px', minWidth: '140px',
  backgroundColor: '#151921', border: '1px solid #1E2330', borderRadius: '10px',
  color: '#E8EAED', fontSize: '0.8rem', outline: 'none',
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

export default function AssetTable({ type, title, icon, chartType }: AssetTableProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [sortField, setSortField] = useState<SortField>('symbol');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [search, setSearch] = useState('');
  const [filterExchange, setFilterExchange] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const sortableFields = SORTABLE_BY_TYPE[type] || ['symbol', 'name'];
  const showRegion = type === 'stock';
  const showWknIsin = type === 'stock' || type === 'bond';

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/market/assets?type=${type}`)
      .then(res => res.json())
      .then(setAssets)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, [type]);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Unique option lists for the filter dropdowns
  const exchangeOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach(a => { if (a.exchange) set.add(a.exchange); });
    return Array.from(set).sort();
  }, [assets]);
  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach(a => { if (a.region) set.add(a.region); });
    return Array.from(set).sort();
  }, [assets]);
  const currencyOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach(a => { if (a.currency) set.add(a.currency); });
    return Array.from(set).sort();
  }, [assets]);

  const sortedAssets = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = assets.filter(a => {
      if (filterExchange && a.exchange !== filterExchange) return false;
      if (filterRegion && a.region !== filterRegion) return false;
      if (filterCurrency && a.currency !== filterCurrency) return false;
      if (q) {
        const hit = [a.symbol, a.name, a.isin, a.wkn]
          .some(v => v && String(v).toLowerCase().includes(q));
        if (!hit) return false;
      }
      return true;
    });
    filtered.sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv), 'de', { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return filtered;
  }, [assets, sortField, sortDir, search, filterExchange, filterRegion, filterCurrency]);

  const hasFilters = !!(search || filterExchange || filterRegion || filterCurrency);

  const totalCount = sortedAssets.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pagedAssets = useMemo(
    () => sortedAssets.slice(pageStart, pageStart + pageSize),
    [sortedAssets, pageStart, pageSize]
  );

  // Reset to page 1 whenever filters/sort/search or the data itself changes
  useEffect(() => {
    setPage(1);
  }, [search, filterExchange, filterRegion, filterCurrency, sortField, sortDir, pageSize, assets]);

  const colCount = 8 + (showRegion ? 1 : 0) + (showWknIsin ? 2 : 0);

  const renderSortHeader = (field: SortField, label: string, extraStyle: React.CSSProperties = {}) => {
    const sortable = sortableFields.includes(field);
    const active = sortField === field;
    return (
      <th
        style={{
          ...HEADER_STYLE,
          ...extraStyle,
          cursor: sortable ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={sortable ? () => handleSort(field) : undefined}
      >
        {label}
        {sortable && (
          <i
            className={
              active
                ? sortDir === 'asc'
                  ? 'fa-solid fa-sort-up'
                  : 'fa-solid fa-sort-down'
                : 'fa-solid fa-sort'
            }
            style={{
              marginLeft: '6px',
              fontSize: '0.7rem',
              color: active ? '#00D97E' : '#2D3240',
            }}
          ></i>
        )}
      </th>
    );
  };

  if (loading) {
    return <div style={{ color: '#4A5068', padding: '40px', textAlign: 'center' }}>Lade {title}...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '4px' }}>
            <i className={icon} style={{ marginRight: '10px', color: '#00D97E' }}></i>{title}
          </h1>
          <p style={{ color: '#7B8399', fontSize: '0.8rem' }}>
            {hasFilters ? `${sortedAssets.length} / ${assets.length}` : assets.length} Assets
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            backgroundColor: '#00D97E', color: '#0B0E14', fontSize: '0.8rem',
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          <i className="fa-solid fa-plus" style={{ marginRight: '6px' }}></i>Hinzufügen
        </button>
      </div>

      {showAdd && (
        <AddAssetDialog
          assetType={type}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#4A5068', fontSize: '0.75rem' }}></i>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Symbol, Name, ISIN oder WKN..."
            style={{
              width: '100%', padding: '9px 12px 9px 34px',
              backgroundColor: '#151921', border: '1px solid #1E2330', borderRadius: '10px',
              color: '#E8EAED', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
              border: 'none', background: 'none', color: '#4A5068', cursor: 'pointer', fontSize: '0.8rem',
            }}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>
        <select value={filterExchange} onChange={e => setFilterExchange(e.target.value)} style={FILTER_SELECT_STYLE}>
          <option value="">Alle Börsen</option>
          {exchangeOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} style={FILTER_SELECT_STYLE}>
          <option value="">Alle Länder</option>
          {regionOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)} style={FILTER_SELECT_STYLE}>
          <option value="">Alle Währungen</option>
          {currencyOptions.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {hasFilters && (
          <button onClick={() => { setSearch(''); setFilterExchange(''); setFilterRegion(''); setFilterCurrency(''); }} style={{
            padding: '9px 14px', borderRadius: '10px', border: '1px solid #1E2330',
            background: 'transparent', color: '#7B8399', fontSize: '0.75rem', fontWeight: 600,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <i className="fa-solid fa-xmark" style={{ marginRight: '6px' }}></i>Zurücksetzen
          </button>
        )}
      </div>

      <div style={{
        backgroundColor: '#151921',
        border: '1px solid #1E2330',
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {renderSortHeader('symbol', 'Symbol')}
              {renderSortHeader('name', 'Name')}
              <th style={HEADER_STYLE}>Börse</th>
              <th style={HEADER_STYLE}>Währung</th>
              {showRegion && renderSortHeader('region', 'Region')}
              {showWknIsin && renderSortHeader('wkn', 'WKN')}
              {showWknIsin && renderSortHeader('isin', 'ISIN')}
              <th style={{ ...HEADER_STYLE, textAlign: 'right' }}>EOD Kurs</th>
              <th style={HEADER_STYLE}>EOD Datum</th>
              <th style={{ ...HEADER_STYLE, textAlign: 'right' }}>Datenpunkte</th>
              <th style={{ ...HEADER_STYLE, textAlign: 'center', width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {pagedAssets.map(asset => (
              <React.Fragment key={asset.id}>
                <tr
                  style={{
                    borderBottom: expandedId === asset.id ? 'none' : '1px solid #1E2330',
                    backgroundColor: expandedId === asset.id ? '#1A1F2B' : 'transparent',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (expandedId !== asset.id) e.currentTarget.style.backgroundColor = '#1A1F2B';
                  }}
                  onMouseLeave={e => {
                    if (expandedId !== asset.id) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={{ ...COL_STYLE, fontWeight: 700, color: '#00D97E' }}>{asset.symbol}</td>
                  <td style={{ ...COL_STYLE, fontWeight: 600 }}>{asset.name}</td>
                  <td style={{ ...COL_STYLE, color: '#7B8399' }}>{asset.exchange || '—'}</td>
                  <td style={COL_STYLE}>
                    <span style={{
                      padding: '2px 6px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                      backgroundColor: '#1E2330', color: '#E8EAED',
                    }}>
                      {asset.currency}
                    </span>
                  </td>
                  {showRegion && (
                    <td style={{ ...COL_STYLE, color: '#7B8399' }}>{asset.region || '—'}</td>
                  )}
                  {showWknIsin && (
                    <td style={{ ...COL_STYLE, color: '#7B8399', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {asset.wkn || '—'}
                    </td>
                  )}
                  {showWknIsin && (
                    <td style={{ ...COL_STYLE, color: '#7B8399', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {asset.isin || '—'}
                    </td>
                  )}
                  <td style={{ ...COL_STYLE, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                    {asset.eod_price
                      ? asset.eod_price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '—'}
                  </td>
                  <td style={{ ...COL_STYLE, color: '#7B8399', fontSize: '0.7rem' }}>
                    {asset.eod_date || '—'}
                  </td>
                  <td style={{ ...COL_STYLE, textAlign: 'right', fontFamily: 'monospace', color: '#7B8399' }}>
                    {asset.candle_count.toLocaleString('de-DE')}
                  </td>
                  <td style={{ ...COL_STYLE, textAlign: 'center' }}>
                    <button
                      onClick={() => toggleExpand(asset.id)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: 'none',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: expandedId === asset.id ? '#00D97E' : '#1E2330',
                        color: expandedId === asset.id ? '#0B0E14' : '#7B8399',
                        transition: 'all 0.15s',
                      }}
                    >
                      {expandedId === asset.id ? 'Schließen' : 'Chart'}
                    </button>
                  </td>
                </tr>

                {/* Expanded chart row */}
                {expandedId === asset.id && (
                  <tr>
                    <td colSpan={colCount} style={{
                      padding: '0 12px 16px',
                      backgroundColor: '#1A1F2B',
                      borderBottom: '1px solid #1E2330',
                    }}>
                      <PriceChart
                        symbol={asset.symbol}
                        name={asset.name}
                        type={chartType}
                        defaultPeriod="MAX"
                        height={350}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {totalCount > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
            borderTop: '1px solid #1E2330',
            fontSize: '0.75rem',
            color: '#7B8399',
            flexWrap: 'wrap',
          }}>
            <div>
              {pageStart + 1}–{Math.min(pageStart + pageSize, totalCount)} von {totalCount.toLocaleString('de-DE')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span>Pro Seite:</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                style={{ ...FILTER_SELECT_STYLE, minWidth: '70px', padding: '6px 10px' }}
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>

              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                style={{
                  padding: '6px 10px', borderRadius: '8px', border: '1px solid #1E2330',
                  background: 'transparent', color: currentPage <= 1 ? '#2D3240' : '#E8EAED',
                  cursor: currentPage <= 1 ? 'default' : 'pointer', fontSize: '0.75rem',
                }}
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>

              <span>Seite</span>
              <select
                value={currentPage}
                onChange={e => setPage(Number(e.target.value))}
                style={{ ...FILTER_SELECT_STYLE, minWidth: '70px', padding: '6px 10px' }}
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <span>von {totalPages}</span>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                style={{
                  padding: '6px 10px', borderRadius: '8px', border: '1px solid #1E2330',
                  background: 'transparent', color: currentPage >= totalPages ? '#2D3240' : '#E8EAED',
                  cursor: currentPage >= totalPages ? 'default' : 'pointer', fontSize: '0.75rem',
                }}
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
